import React, { useEffect, useMemo, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { getProductValidationSchema } from "../../../validations/LineItemValidation";
import { Divider, Grid, Paper, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import UOMSelect from "../../../components/form/UOMSelect";
import Button from "@mui/material/Button";
import CustomTextInput from "../../../components/CustomTextInput";
import TaxCategoryFields from "../../../components/TaxCategoryField";
import ItemTypeSelect from "../../../components/form/ItemTypeSelect";
import { useMutateProductHandler } from "../dataHandlers/mutateProductHandler";
import {useDispatch, useSelector} from "react-redux";
import {
  getCountryOptionByCode,
  getUOMOptionByCode,
} from "../../../api/codelist/codeListsSlice";
import { map } from "lodash";
import { DEFAULT_TAX_SCHEME } from "../../../constants/defaults";
import NatureCodeSelect from "../../../components/form/NatureCodeSelect";
import CommodityCodeSelect from "../../../components/form/CommodityCodeSelect";
import CommodityClassificationCodeSelect from "../../../components/form/ItemClassificationCodeSelect";
import ItemClassificationSchemeIDSelect from "../../../components/form/ItemClassificationSchemeIDSelect";
import CountrySelect from "../../../components/form/CountrySelect";
import {showSnackbar} from "../../../api/snackbarSlice";
import {usePermissions} from "../../../hooks/usePermissions";
import { CREATE_PRODUCT} from "../../../constants/eInvoicePermissions";
import {useNavigate} from "react-router-dom";

const createClassifiedTaxCategory = (source = {}) => ({
  id: source?.id ?? null,
  percent: Number(source?.percent ?? 0) || 0,
  tax_exemption_reason_code: source?.tax_exemption_reason_code ?? null,
  tax_exemption_reason: source?.tax_exemption_reason ?? null,
  zero_rated_reason_code: source?.zero_rated_reason_code ?? null,
  zero_rated_reason: source?.zero_rated_reason ?? null,
  tax_scheme: source?.tax_scheme ?? DEFAULT_TAX_SCHEME,
});

const createEmptyProduct = () => ({
  unit_of_measure: null,
  name: "",
  description: "",
  quantity: 1,
  unit_price: 1,
  classified_tax_category: createClassifiedTaxCategory(),
  additional_item_identification: {
    nature_code: "",
  },
  item_specification_document_reference: {
    commodity_code: "",
    item_type: "G",
  },
  commodity_classification: {
    item_classification_code: "",
    item_classification_list_id: "HS",
    item_classification_list_version_id: "1.0",
  },
});

const buildFormValues = (data = {}, uomOption, countryCode) => {
  const classified = data?.classified_tax_category;

  return {
    ...createEmptyProduct(),
    ...data,
    unit_of_measure: uomOption ?? data.unit_of_measure ?? null,
    origin_country: {
      identification_code: countryCode,
    },
    classified_tax_category: createClassifiedTaxCategory(classified),
    additional_item_identification: {
      ...createEmptyProduct().additional_item_identification,
      ...(data.additional_item_identification || {}),
    },
    item_specification_document_reference: {
      ...createEmptyProduct().item_specification_document_reference,
      ...(data.item_specification_document_reference || {}),
    },
    commodity_classification: {
      ...createEmptyProduct().commodity_classification,
      ...(data.commodity_classification || {}),
    },
  };
};

const ProductsForm = ({ handleClose, initialData, readOnly = false }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {hasEInvoicePermission} = usePermissions();
  const canCreateProducts = hasEInvoicePermission(CREATE_PRODUCT);
  const productSchema = useMemo(() => getProductValidationSchema(), []);
  const normalizedInitialData = useMemo(
    () => initialData ?? createEmptyProduct(),
    [initialData],
  );
  const uomOption = useSelector(
    getUOMOptionByCode(normalizedInitialData.unit_of_measure),
  );

  const countryCode = useSelector(
    getCountryOptionByCode(
      normalizedInitialData?.origin_country?.identification_code,
    ),
  );

  const defaultValues = useMemo(
    () => buildFormValues(normalizedInitialData, uomOption, countryCode),
    [normalizedInitialData, uomOption, countryCode],
  );
  const [commodityData, setCommodityData] = useState(null);
  const [natureCodeData, setNatureCodeData] = useState(null);

  const {
    handleSubmit,
    control,
    setValue,
    reset,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues,
    resolver: yupResolver(productSchema),
  });
  const itemType = useWatch({
    control,
    name: "item_specification_document_reference.item_type",
  });

  const onProductCreated = ({ addOneMore }) => {
    if (addOneMore) {
      reset(buildFormValues(createEmptyProduct(), null));
    } else {
      handleClose();
    }
  };

  const { onSubmit, error, isPending } = useMutateProductHandler({
    onSuccess: onProductCreated,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if(!canCreateProducts) {
      dispatch(
          showSnackbar({
            message: "Insufficient Permission.",
            severity: 'error',
          }),
      );
      navigate(
         "/products"
      );
    }
  }, [canCreateProducts]);

  useEffect(() => {
    if (!isPending && isSubmitting) {
      setIsSubmitting(false);
    }
  }, [isPending, isSubmitting]);

  useEffect(() => {
    map(error, (value, key) => {
      setError(key, { message: value, type: "string" });
    });
  }, [setError, error]);

  const handleSave = async (data, addOneMore) => {
    setIsSubmitting(true);
    const classifiedTaxCategory = createClassifiedTaxCategory(
      data.classified_tax_category ?? data,
    );

    const itemData = {
      ...data,
      unit_of_measure: data.unit_of_measure?.code || null,
      classified_tax_category: classifiedTaxCategory,
      origin_country: {
        identification_code: data.origin_country?.identification_code?.code,
      },
      additional_item_identification: {
        nature_code: data.additional_item_identification?.nature_code || null,
      },
      item_specification_document_reference: {
        commodity_code:
          data.item_specification_document_reference?.commodity_code || null,
        item_type:
          data.item_specification_document_reference?.item_type || null,
      },
      commodity_classification: {
        item_classification_code:
          data.commodity_classification?.item_classification_code || null,
        item_classification_list_id:
          data.commodity_classification?.item_classification_list_id || null,
        item_classification_list_version_id:
          data.commodity_classification?.item_classification_list_version_id ||
          "1.0",
      },
    };
    onSubmit(itemData, addOneMore);
  };

  return (
    <>
      <Paper elevation={0} sx={{ p: "1rem", overflow: "auto" }}>
        <Box sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 6 }}>
              <CustomTextInput
                control={control}
                errors={errors}
                data-testid="name-input"
                name="name"
                readOnly={readOnly}
                label="Item Name"
              />
            </Grid>

            <Grid size={{ xs: 6 }}>
              <CustomTextInput
                data-testid="description-input"
                control={control}
                errors={errors}
                name="description"
                readOnly={readOnly}
                label="Description"
              />
            </Grid>

            <Grid size={{ xs: 4 }}>
              <UOMSelect
                control={control}
                errors={errors}
                data-testid="unit-of-measure-select"
                name="unit_of_measure"
                readOnly={readOnly}
              />
            </Grid>

            <Grid size={{ xs: 4 }}>
              <CustomTextInput
                control={control}
                errors={errors}
                data-testid="quantity-input"
                name="quantity"
                type="number"
                readOnly={readOnly}
                label="Quantity"
              />
            </Grid>

            <Grid size={{ xs: 4 }}>
              <CustomTextInput
                control={control}
                errors={errors}
                data-testid="unit-price-input"
                name="unit_price"
                type="number"
                readOnly={readOnly}
                label="Unit Price"
              />
            </Grid>

            <Grid size={4}>
              <Controller
                name="additional_item_identification.nature_code"
                control={control}
                render={({ field }) => (
                  <NatureCodeSelect
                    control={control}
                    errors={errors}
                    name="additional_item_identification.nature_code"
                    readOnly={readOnly}
                    helperText=""
                    onChange={(newCode) => {
                      field.onChange(newCode);
                    }}
                    onCodesLoaded={setNatureCodeData}
                  />
                )}
              />
            </Grid>
            <Grid size={4}>
              <Controller
                name="item_specification_document_reference.commodity_code"
                control={control}
                render={({ field }) => (
                  <CommodityCodeSelect
                    control={control}
                    errors={errors}
                    name="item_specification_document_reference.commodity_code"
                    readOnly={readOnly}
                    helperText=""
                    onChange={(newCode) => {
                      field.onChange(newCode);
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={4}>
              <Controller
                name="item_specification_document_reference.item_type"
                control={control}
                render={({ field }) => (
                  <ItemTypeSelect
                    control={control}
                    errors={errors}
                    name="item_specification_document_reference.item_type"
                    readOnly={readOnly}
                    helperText=""
                    onChange={(newCode) => {
                      field.onChange(newCode);
                      setValue(
                        "commodity_classification.item_classification_list_id",
                        newCode === "S" ? "MP" : "HS",
                      );
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={4}>
              <Controller
                name="commodity_classification.item_classification_code"
                control={control}
                render={({ field }) => (
                  <CommodityClassificationCodeSelect
                    control={control}
                    errors={errors}
                    name="commodity_classification.item_classification_code"
                    readOnly={readOnly}
                    helperText=""
                    onChange={(newCode) => {
                      field.onChange(newCode);
                    }}
                    label="Item Classification Code"
                    onCodesLoaded={setCommodityData}
                  />
                )}
              />
            </Grid>

            <Grid size={4}>
              <Controller
                name="commodity_classification.item_classification_list_id"
                control={control}
                render={({ field }) => (
                  <ItemClassificationSchemeIDSelect
                    control={control}
                    errors={errors}
                    name="commodity_classification.item_classification_list_id"
                    readOnly={readOnly}
                    helperText=""
                    allowedCodes={itemType === "S" ? ["MP"] : ["HS"]}
                    onChange={(newCode) => {
                      field.onChange(newCode);
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={4}>
              <Controller
                name="origin_country.identification_code"
                control={control}
                render={({ field }) => (
                  <CountrySelect
                    control={control}
                    label="Origin Country"
                    errors={errors}
                    name="origin_country.identification_code"
                    readOnly={readOnly}
                    helperText=""
                    onChange={(newCode) => {
                      field.onChange(newCode);
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Typography variant="h6" sx={{ mb: "1.5rem", pt: "1rem" }}>
            Taxes
            <Divider />
          </Typography>

          <Grid container spacing={2} mt={1}>
            <TaxCategoryFields
              control={control}
              errors={errors}
              data-testid="tax-category-select"
              taxCategoryName="classified_tax_category.id"
              taxRateName="classified_tax_category.percent"
              setValue={setValue}
              taxCategoryReadOnly={readOnly}
            />
          </Grid>
        </Box>
      </Paper>
      <Divider />
      <Box
        sx={{
          px: 2,
          py: 2,
          borderTop: "1px solid #e0e0e0",
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
        }}
      >
        {!readOnly && (
          <>
            <Button
              data-testid="save-and-add-new-product-button"
              type="button"
              variant="contained"
              color="primary"
              onClick={handleSubmit((data) => handleSave(data, true))}
              disabled={isPending || isSubmitting}
            >
              Save & Add new
            </Button>
            <Button
              data-testid="save-product-button"
              type="button"
              variant="contained"
              color="primary"
              onClick={handleSubmit((data) => handleSave(data, false))}
              disabled={isPending || isSubmitting}
            >
              Save
            </Button>
          </>
        )}
      </Box>
    </>
  );
};

export default ProductsForm;
